import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'

export type ChatRoomType = 'direct' | 'group'
export type ChatRoomMemberRole = 'member' | 'admin'
export type ChatAttachmentType = 'image' | 'file'

export interface ChatAttachment {
  type: ChatAttachmentType
  path: string
  name: string
  url: string | null
  mime: string | null
  size: number | null
}

export interface ChatMessageAuthor {
  id: number
  full_name: string
  avatar_url: string | null
}

export interface ChatMessage {
  id: number
  chat_room_id: number
  author_user_id: number
  text: string | null
  attachments: ChatAttachment[]
  is_deleted: boolean
  edited_at: string | null
  created_at: string | null
  updated_at: string | null
  author?: ChatMessageAuthor | null
}

export interface ChatRoomMember {
  user_id: number
  role: ChatRoomMemberRole
  last_read_message_id: number | null
  muted_until: string | null
  user: ChatMessageAuthor | null
}

export interface ChatRoom {
  id: number
  type: ChatRoomType
  student_group_id: number | null
  unread_count: number
  can_write: boolean
  is_muted: boolean
  title: string | null
  group: { id: number; name: string } | null
  members?: ChatRoomMember[]
  last_message?: ChatMessage | null
  created_at: string | null
  updated_at: string | null
}

export interface ChatContact {
  user: ChatMessageAuthor
  existing_room_id: number | null
}

export interface MessagesPage {
  data: ChatMessage[]
  meta: {
    has_more: boolean
    next_before_id: number | null
  }
}

export interface SendMessagePayload {
  text?: string | null
  attachments?: Array<{
    type: ChatAttachmentType
    path: string
    name: string
    mime: string
    size: number
  }>
}

interface ApiEnvelope<T> {
  message?: string
  data?: T
}

function unwrapData<T>(response: { data: ApiEnvelope<T> }, fallbackMessage: string): T {
  if (response.data?.data !== undefined) {
    return response.data.data
  }
  throw new Error(response.data?.message || fallbackMessage)
}

export const chatApi = {
  getRooms: async (): Promise<ChatRoom[]> => {
    const response = await apiClient.get<ApiEnvelope<ChatRoom[]>>(API_ENDPOINTS.CHAT.ROOMS)
    return response.data.data ?? []
  },

  getContacts: async (): Promise<ChatContact[]> => {
    const response = await apiClient.get<ApiEnvelope<ChatContact[]>>(API_ENDPOINTS.CHAT.CONTACTS)
    return response.data.data ?? []
  },

  openDirect: async (userId: number): Promise<ChatRoom> => {
    const response = await apiClient.post<ApiEnvelope<ChatRoom>>(
      API_ENDPOINTS.CHAT.OPEN_DIRECT(userId)
    )
    return unwrapData(response, '')
  },

  getMessages: async (
    roomId: number,
    params?: { before_id?: number; limit?: number }
  ): Promise<MessagesPage> => {
    const search = new URLSearchParams()
    if (params?.before_id) search.append('before_id', String(params.before_id))
    if (params?.limit) search.append('limit', String(params.limit))
    const query = search.toString()
    const url = query
      ? `${API_ENDPOINTS.CHAT.MESSAGES(roomId)}?${query}`
      : API_ENDPOINTS.CHAT.MESSAGES(roomId)
    const response = await apiClient.get<MessagesPage>(url)
    return response.data
  },

  sendMessage: async (roomId: number, payload: SendMessagePayload): Promise<ChatMessage> => {
    const response = await apiClient.post<ApiEnvelope<ChatMessage>>(
      API_ENDPOINTS.CHAT.MESSAGES(roomId),
      payload
    )
    return unwrapData(response, '')
  },

  editMessage: async (messageId: number, payload: SendMessagePayload): Promise<ChatMessage> => {
    const response = await apiClient.patch<ApiEnvelope<ChatMessage>>(
      API_ENDPOINTS.CHAT.MESSAGE(messageId),
      payload
    )
    return unwrapData(response, '')
  },

  deleteMessage: async (messageId: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.CHAT.MESSAGE(messageId))
  },

  markRead: async (roomId: number, upTo: number): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.CHAT.READ(roomId), { up_to: upTo })
  },

  enterPresence: async (roomId: number): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.CHAT.PRESENCE(roomId))
  },

  leavePresence: async (roomId: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.CHAT.PRESENCE(roomId))
  },

  uploadFile: async (roomId: number, file: File): Promise<ChatAttachment> => {
    const form = new FormData()
    form.append('file', file)
    const response = await apiClient.post<ApiEnvelope<ChatAttachment>>(
      API_ENDPOINTS.CHAT.UPLOAD(roomId),
      form
    )
    return unwrapData(response, '')
  },

  mute: async (roomId: number, until: string | null): Promise<void> => {
    await apiClient.post(API_ENDPOINTS.CHAT.MUTE(roomId), { until })
  },
}

